import { useRef, useState } from "react";
import { Alert, Card, Divider, Flex, InputRef } from "antd";
import {
  CheckCircleOutlined,
  GiftOutlined,
  PercentageOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import MetricCard from "components/MetricCard.component";
import { getLoyaltyAnalytics } from "hooks/queries/analytics";
import { useTableSort } from "hooks/useTableSort";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { formatCurrency } from "shared/utils";
import { rewardLabel } from "shared/utils/loyalty";
import { tnum } from "shared/components/TableText";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { AnalyticsRange, CustomDayRange, LoyaltyAnalytics, LoyaltyClaimRow } from "shared/types";
import PATHS from "shared/constants/paths";
import { tzDate, getYtdDayRange } from "shared/utils/dayjs";
import { analyticsRangeStamp } from "shared/utils/download";
import { MAX_PDF_ROWS } from "pdf/layout";
import { useAuth } from "context/AuthContextProvider";
import RangeSegmented from "./RangeSegmented";
import CustomRangePicker from "./CustomRangePicker";
import ReportPageHeading from "./ReportPageHeading";
import ExportMenu from "./ExportMenu.component";
import StampDistributionChart from "./StampDistributionChart";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { ReportColumn, reportColumn } from "shared/reports/reportColumns";
import { ReportTable } from "shared/reports/ReportTable";
import { formatMoneyOrDash } from "shared/reports/columnCatalogue";
import { loyaltySpec } from "pdf/reports/specs/reward";
import { useReportPageCount } from "./useReportPageCount";
import { textPx } from "shared/constants/zoom";

// The chart still renders at 1.4x the shared viewBox HEIGHT, but it now sizes
// its own viewBox WIDTH to its container, so there is no width to reserve and
// no letterbox to leave room for.
const CHART_HEIGHT = 280;

// The two facts the axis cannot carry, sitting on the heading line. The final
// milestone in particular is off the axis BY DESIGN (a card closes the moment
// it issues, so no open card is ever there), and it is the one clients are
// actually working towards.
const SpecFact = ({ label, value }: { label: string; value: string }) => (
  <Flex align="baseline" gap={6}>
    <span style={{ fontSize: textPx(12), color: colors.ink3 }}>{label}</span>
    <span style={{ fontSize: textPx(12), fontWeight: 600, color: colors.ink }}>{value}</span>
  </Flex>
);

const CardSpec = ({ dist }: { dist?: LoyaltyAnalytics["distribution"] }) => {
  if (!dist) return null;
  const final = dist.milestones.find((m) => m.stamp_number === dist.total_stamps);
  return (
    <Flex align="baseline" gap={20} wrap="wrap">
      <SpecFact
        label={translate("analyticsPage.loyalty.spec_card_size")}
        value={String(dist.total_stamps)}
      />
      {final && (
        <SpecFact
          label={translate("analyticsPage.loyalty.spec_final_reward")}
          value={rewardLabel(final)}
        />
      )}
    </Flex>
  );
};

const LoyaltyPerformancePage = () => {
  const { state: authState } = useAuth();
  const navigate = useNavigate();
  const { sortState, onSortChange } = useTableSort();
  const ytdRange = getYtdDayRange();
  const [range, setRange] = useState<AnalyticsRange>("ytd");
  const [customRange, setCustomRange] = useState<CustomDayRange>(ytdRange);
  const isYtdActive =
    customRange?.from === ytdRange.from && customRange?.to === ytdRange.to;
  const searchInput = useRef<InputRef>(null);
  const { data, isLoading } = getLoyaltyAnalytics(range, customRange);

  const windowParams = customRange
    ? { from_date: customRange.from, to_date: customRange.to }
    : { range };

  const columns: ReportColumn<LoyaltyClaimRow>[] = [
    reportColumn<LoyaltyClaimRow>("claimed_at", {
      dataIndex: "redeemed_at",
      sorter: (a, b) => tzDate(a.redeemed_at).valueOf() - tzDate(b.redeemed_at).valueOf(),
      defaultSortOrder: "descend",
    }),
    reportColumn<LoyaltyClaimRow>("client", {
      dataIndex: "customer_name",
      search: getColumnSearchProps<LoyaltyClaimRow, "customer_name">({
        config: "customer_name",
        searchInput,
      }),
    }),
    reportColumn<LoyaltyClaimRow>("milestone", {
      dataIndex: "stamp_number",
      render: (value: number | null) => <span style={tnum}>{value ?? "-"}</span>,
      sorter: (a, b) => (a.stamp_number ?? 0) - (b.stamp_number ?? 0),
    }),
    reportColumn<LoyaltyClaimRow>("reward", {
      key: "reward",
      // rewardLabel is the same formatter the client detail page uses, so a
      // reward reads identically on both surfaces.
      render: (_: unknown, row) => rewardLabel(row),
    }),
    reportColumn<LoyaltyClaimRow>("discount", {
      dataIndex: "discount_amount",
      // A free credit has no rupiah value, so a dash rather than Rp 0 (see
      // the "credits are counted, never valued" rule).
      render: (value: number | null) => <span style={tnum}>{formatMoneyOrDash(value)}</span>,
      sorter: (a, b) => (a.discount_amount ?? -1) - (b.discount_amount ?? -1),
    }),
    reportColumn<LoyaltyClaimRow>("claimed_by", {
      dataIndex: "redeemed_by_name",
      // A client who claimed a free credit in the app is stamped as their own
      // user, so this column shows their name. That is the truth of who
      // claimed it, not a mis-join.
      render: (value: string | null) => value ?? "-",
    }),
  ];

  const dist = data?.distribution;

  const pageCount = useReportPageCount(loyaltySpec, data, sortState);

  return (
    <>
      <ReportPageHeading
        title={translate("analyticsPage.loyalty.report_performance")}
        backTo={PATHS.ANALYTICS}
        extra={
          <ExportMenu
            reportSlug="performa-loyalty-card"
            params={windowParams}
            pdf={
              data && {
                rowChoice: {
                  pagesFor: pageCount.pagesFor,
                  prepare: pageCount.prepare,
                  available: Math.min(data.table.length, MAX_PDF_ROWS),
                  total: data.table_total,
                },
                fileName: `spott-performa-loyalty-card-${analyticsRangeStamp(range, customRange)}.pdf`,
                render: async (maxRows) => {
                  const { renderReportPdf } = await loadReportPdf();
                  return renderReportPdf({
                    slug: "performa-loyalty-card",
                    studio: authState.business?.name ?? "",
                    data,
                    sortState,
                    maxRows,
                  });
                },
              }
            }
          />
        }
      />
      <Flex vertical gap={20}>
        {data?.program_status === "off" && (
          <Alert
            type="info"
            showIcon
            message={translate("analyticsPage.loyalty.program_off")}
          />
        )}
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <span style={{ fontSize: textPx(13), color: colors.ink2 }}>{data?.range_label}</span>
          <Flex align="center" gap={10} wrap="wrap">
            <CustomRangePicker
              value={isYtdActive ? null : customRange}
              onChange={setCustomRange}
            />
            <RangeSegmented
              value={isYtdActive ? "ytd" : range}
              active={!customRange || isYtdActive}
              onChange={(value) => {
                setCustomRange(value === "ytd" ? ytdRange : null);
                setRange(value);
              }}
            />
          </Flex>
        </Flex>

        <Flex gap={16} wrap="wrap">
          <MetricCard
            icon={<CheckCircleOutlined />}
            title={translate("analyticsPage.loyalty.kpi_cards_completed")}
            information={translate("analyticsPage.loyalty.kpi_cards_completed_info")}
            value={data?.cards_completed.value ?? 0}
          />
          <MetricCard
            icon={<GiftOutlined />}
            title={translate("analyticsPage.loyalty.kpi_credits_granted")}
            information={translate("analyticsPage.loyalty.kpi_credits_granted_info")}
            value={data?.credits_granted.value ?? 0}
          />
          <MetricCard
            icon={<PercentageOutlined />}
            title={translate("analyticsPage.loyalty.kpi_discount_given")}
            information={translate("analyticsPage.loyalty.kpi_discount_given_info")}
            value={formatCurrency(data?.discount_value_given.value ?? 0)}
          />
        </Flex>

        {/* The chart is a snapshot of right now, not of the selected range;
            its own subtitle says so. It sits ABOVE the claims table: it
            describes the programme's current state, which is the thing an
            admin comes here to read, and the table is the log underneath. */}
        <Card styles={{ body: { padding: 22 } }} loading={isLoading}>
          <Flex justify="space-between" align="baseline" gap={20} wrap="wrap">
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 600,
                fontSize: textPx(16),
                color: colors.ink,
              }}
            >
              {translate("analyticsPage.loyalty.distribution_heading")}
            </div>
            <CardSpec dist={dist} />
          </Flex>
          <div style={{ fontSize: textPx(12), color: colors.ink3, marginTop: 4, marginBottom: 18 }}>
            {translate("analyticsPage.loyalty.distribution_desc")}
          </div>
          {/* Full width: the chart sizes its own viewBox to its container, so
              it fills the card instead of floating in a letterbox. */}
          <StampDistributionChart
            buckets={dist?.buckets ?? []}
            milestones={dist?.milestones ?? []}
            height={CHART_HEIGHT}
          />
          {/* Clients left off the chart are STATED, not just absent: they are
              running a card frozen on an older config, and pooling them onto
              this axis would put two card lengths in one column. Hidden at
              zero, since it is self-healing and there is nothing to act on. */}
          {!!dist?.excluded_old_config && (
            <div style={{ fontSize: textPx(12), color: colors.ink3, marginTop: 14 }}>
              {`${translate("analyticsPage.loyalty.spec_old_structure")}: ${dist.excluded_old_config} ${translate("analyticsPage.loyalty.client_unit")}`}
            </div>
          )}
        </Card>

        <Divider style={{ margin: "4px 0 0" }} />
        <Card styles={{ body: { padding: 18 } }}>
          <ReportTable<LoyaltyClaimRow>
            serverTotal={{ total: data?.table_total, noun: translate("pdf.noun.klaim") }}
            rowKey="id"
            loading={isLoading}
            columns={columns}
            dataSource={data?.table ?? []}
            onRow={(row) => ({
              onClick: () =>
                navigate(`${PATHS.CUSTOMER}/${row.customer_id}?tab=5&reward=loyalty`),
              style: { cursor: "pointer" },
            })}
            pageMemory="analyticsLoyaltyPerformance"
            onSortChange={onSortChange}
          />
        </Card>
      </Flex>
    </>
  );
};

export default LoyaltyPerformancePage;
